/**
 * Perfil: lo que se pregunta y cómo se valida (Req. 4; formulario depurado,
 * 0.3.6).
 *
 * Primeros pasos y Más → Perfil preguntan solo lo que ya cambia algo en la
 * app: el nombre y las unidades. La estatura, la fecha de nacimiento, el sexo
 * y la actividad sirven solo para cálculos (IMC y mantenimiento estimado) y
 * se piden en el cálculo que los usa; lo que ya estaba guardado se conserva.
 * Por eso su validación vive aquí, aparte del formulario.
 *
 * Todo es opcional (Req. 4.2). Lo que sí se valida es que lo escrito tenga
 * sentido, para que un cálculo no parta de un error de captura (por ejemplo,
 * 16 en lugar de 160 cm).
 */

import type { NivelActividad, Sexo } from '@/db/types';
import { diferenciaDias, esFechaLocal, type FechaLocal } from './dates';
import { aCm, desdeCm, type Capturado, type UnidadLongitud, type UnidadPeso } from './units';
import { leerDecimal, formatearNumero } from './numbers';

/** Rango de estatura aceptado, en cm. */
export const ESTATURA_MIN_CM = 100;
export const ESTATURA_MAX_CM = 250;
/** Rango de edad aceptado, en años. */
export const EDAD_MIN = 10;
export const EDAD_MAX = 110;
export const NOMBRE_MAX = 60;

/**
 * Edad en años cumplidos a una fecha (Req. 4.6). Quien nació un 29 de febrero
 * cumple, en los años no bisiestos, el 1 de marzo.
 */
export function edadEn(fechaNacimiento: FechaLocal, fecha: FechaLocal): number {
  const [an, mn, dn] = fechaNacimiento.split('-').map(Number) as [number, number, number];
  const [a, m, d] = fecha.split('-').map(Number) as [number, number, number];
  let edad = a - an;
  if (m < mn || (m === mn && d < dn)) edad -= 1;
  return edad;
}

// ---------------------------------------------------------------------------
// Primeros pasos y Más → Perfil
// ---------------------------------------------------------------------------

/** Lo que se escribe en el formulario del perfil. Vacío = sin dato. */
export interface CapturaPerfil {
  nombre: string;
  unidadPeso: UnidadPeso;
  unidadLongitud: UnidadLongitud;
  /**
   * Datos que alimentan un cálculo concreto (0.11.0): la edad, el sexo y la
   * actividad entran en la fórmula del mantenimiento estimado. Siguen siendo
   * opcionales y el formulario los trae precargados.
   */
  fechaNacimiento: string;
  sexo: Sexo | '';
  nivelActividad: NivelActividad | '';
}

/**
 * Lo que el formulario guarda en los ajustes. La estatura no va aquí: se
 * valida aparte con `validarEstatura` y solo se escribe si lo escrito cambió,
 * para que una estatura intacta no se vuelva a convertir (0.4.0).
 */
export interface CambiosPerfil {
  nombre: string | null;
  unidadPeso: UnidadPeso;
  unidadLongitud: UnidadLongitud;
  fechaNacimiento: FechaLocal | null;
  sexo: Sexo | null;
  nivelActividad: NivelActividad | null;
}

export type CampoPerfil = 'nombre' | 'fechaNacimiento';

export type ResultadoPerfil =
  | { valido: true; cambios: CambiosPerfil }
  | { valido: false; errores: Partial<Record<CampoPerfil, string>> };

/** `hoy` es para que la fecha de nacimiento no sea futura ni dé una edad imposible. */
export function validarPerfil(captura: CapturaPerfil, hoy: FechaLocal): ResultadoPerfil {
  const errores: Partial<Record<CampoPerfil, string>> = {};
  const nombre = captura.nombre.trim();
  if (nombre.length > NOMBRE_MAX) {
    errores.nombre = `El nombre no puede pasar de ${NOMBRE_MAX} caracteres.`;
  }
  const nacimiento = validarFechaNacimiento(captura.fechaNacimiento, hoy);
  if (!nacimiento.valido) errores.fechaNacimiento = nacimiento.error;
  if (Object.keys(errores).length > 0) return { valido: false, errores };

  return {
    valido: true,
    cambios: {
      nombre: nombre === '' ? null : nombre,
      unidadPeso: captura.unidadPeso,
      unidadLongitud: captura.unidadLongitud,
      fechaNacimiento: nacimiento.valido ? nacimiento.valor : null,
      sexo: captura.sexo === '' ? null : captura.sexo,
      nivelActividad: captura.nivelActividad === '' ? null : captura.nivelActividad,
    },
  };
}

/**
 * Qué datos del perfil le faltan para que los cálculos funcionen, en el orden
 * del formulario (0.11.0). El peso no entra: vive en los pesajes, con su
 * báscula y su fecha.
 */
export type DatoDePerfil = 'nombre' | 'estatura' | 'nacimiento' | 'sexo' | 'actividad';

export const NOMBRE_DATO_PERFIL: Readonly<Record<DatoDePerfil, string>> = {
  nombre: 'tu nombre',
  estatura: 'tu estatura',
  nacimiento: 'tu fecha de nacimiento',
  sexo: 'tu sexo',
  actividad: 'tu nivel de actividad',
};

export function datosDePerfilQueFaltan(s: {
  nombre: string | null;
  estaturaCm: number | null;
  fechaNacimiento: FechaLocal | null;
  sexo: Sexo | null;
  nivelActividad: NivelActividad | null;
}): DatoDePerfil[] {
  return [
    s.nombre === null || s.nombre.trim() === '' ? ('nombre' as const) : null,
    s.estaturaCm === null ? ('estatura' as const) : null,
    s.fechaNacimiento === null ? ('nacimiento' as const) : null,
    s.sexo === null ? ('sexo' as const) : null,
    s.nivelActividad === null ? ('actividad' as const) : null,
  ].filter((d) => d !== null);
}

// ---------------------------------------------------------------------------
// Datos para cálculos: se piden donde se usan
// ---------------------------------------------------------------------------

function rangoEstatura(unidad: UnidadLongitud): string {
  const min = formatearNumero(desdeCm(ESTATURA_MIN_CM, unidad), { decimales: 1, fijos: false });
  const max = formatearNumero(desdeCm(ESTATURA_MAX_CM, unidad), { decimales: 1, fijos: false });
  return `${min} y ${max} ${unidad}`;
}

export type ResultadoDato<T> = { valido: true; valor: T } | { valido: false; error: string };

/**
 * La estatura escrita en cm o in (Req. 4.3, 5.4): el valor en cm y lo
 * capturado, o por qué no sirve. Vacío = sin dato.
 */
export function validarEstatura(
  texto: string,
  unidad: UnidadLongitud,
): ResultadoDato<{ estaturaCm: number; estaturaCapturada: Capturado } | null> {
  if (texto.trim() === '') return { valido: true, valor: null };
  const valor = leerDecimal(texto);
  if (valor === null) return { valido: false, error: 'Escribe la estatura con números, por ejemplo 160 o 160.5.' };
  const cm = aCm(valor, unidad);
  if (cm < ESTATURA_MIN_CM || cm > ESTATURA_MAX_CM) {
    return { valido: false, error: `La estatura debe estar entre ${rangoEstatura(unidad)}.` };
  }
  return { valido: true, valor: { estaturaCm: cm, estaturaCapturada: { valor, unidad } } };
}

/** La fecha de nacimiento: válida, no futura y con una edad posible. Vacío = sin dato. */
export function validarFechaNacimiento(texto: string, hoy: FechaLocal): ResultadoDato<FechaLocal | null> {
  if (texto === '') return { valido: true, valor: null };
  if (!esFechaLocal(texto)) return { valido: false, error: 'La fecha de nacimiento no es válida.' };
  if (diferenciaDias(texto, hoy) < 0) return { valido: false, error: 'La fecha de nacimiento no puede ser futura.' };
  const edad = edadEn(texto, hoy);
  if (edad < EDAD_MIN || edad > EDAD_MAX) {
    return { valido: false, error: `Revisa la fecha: daría una edad de ${edad} años.` };
  }
  return { valido: true, valor: texto };
}
